import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { HuAutonomousPurchaseForm } from "@/components/ui/forms/huAutonomousPurchase"
import { withForm } from "@/components/ui/forms/hooks/useFormHook"
import { InformationAlertIcon } from "@images/svgIcons/informationAlertIcon"
import { type BoxOfferFormPageProps } from "@modules/boxOffer/components/boxOfferForm"
import { BoxOfferFormSection } from "@modules/boxOffer/components/boxOfferFormSection"
import { boxOfferFormDefaultValues } from "@modules/boxOffer/types/boxOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { HUCardAlert } from "@modules/ui/components/huCardAlert"
import { useStore } from "@tanstack/react-form"
import { ArrowLeftIcon, ArrowRightIcon, EyeIcon } from "lucide-react"
import { useEffect } from "react"

export const BoxOfferFormThirdPage = withForm({
  defaultValues: boxOfferFormDefaultValues,
  props: {} as BoxOfferFormPageProps,
  render: function BoxOfferFormThirdPageInner({ form, handleSubmit, setPageNumber, isArchiveEdition, isEdition }) {
    const type = useStore(form.baseStore, (state) => state.values.type)

    useEffect(() => {
      if (isArchiveEdition) {
        form.setFieldValue("showcaseDisplay", false)
      }
    }, [isArchiveEdition, form])

    return (
      <div className="flex flex-col gap-6 px-5 pt-20 pb-4">
        <div className="flex flex-col gap-2">
          <Text fontStyle="TL">{`${isEdition ? word(`offer.form.${type}.edit`) : word(`offer.form.${type}.create`)} (3/3)`}</Text>
          <Text fontStyle="BM" className="text-textDefault-caption">
            {word(`offer.form.${type}.subtitle`)}
          </Text>
        </div>

        {isArchiveEdition && (
          <HUCardAlert
            className="px-6 py-2"
            title={""}
            description={word("box.offer.form.infos.archivedEdition.information.description")}
            icon={<InformationAlertIcon backgroundColor={""} color="currentColor" />}
          />
        )}
        <div className="flex flex-col gap-7">
          <BoxOfferFormSection
            title={word("box.offer.form.infos.visibility.title")}
            icon={<EyeIcon color="currentColor" className="shrink-0" />}
            description={word("box.offer.form.infos.visibility.subtitle")}
            className="mb-5"
          />
          <form.AppField
            name="showcaseDisplay"
            listeners={{
              onChange: ({ value }) => {
                if (!value) {
                  form.setFieldValue("priceDisplay", false)
                }
              },
            }}
          >
            {(field) => (
              <field.Field>
                <field.Label>{word("box.offer.form.infos.showcaseDisplay.label")}</field.Label>
                <field.Content>
                  <field.SwitchInline
                    textSwitch={word("box.offer.form.infos.showcaseDisplay.textSwitch")}
                    switchProps={{ disabled: isArchiveEdition }}
                  />
                  <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
                    {word("box.offer.form.infos.showcaseDisplay.description")}
                  </Text>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="priceDisplay">
            {(field) => (
              <field.Field>
                <field.Label>{word("box.offer.form.infos.priceDisplay.label")}</field.Label>
                <field.Content>
                  <form.Subscribe selector={(state) => state.values.showcaseDisplay}>
                    {(showcaseDisplay) => (
                      <field.SwitchInline
                        textSwitch={word("box.offer.form.infos.priceDisplay.textSwitch")}
                        switchProps={{ disabled: !showcaseDisplay }}
                      />
                    )}
                  </form.Subscribe>
                  <Text fontStyle="BS" className="px-5 text-justify leading-5 text-textDefault-caption">
                    {word("box.offer.form.infos.priceDisplay.description")}
                  </Text>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>

          <form.AppField name="autonomousPurchase">
            {(field) => (
              <field.Field>
                <field.Label required>{word("box.offer.form.infos.autonomousPurchase.label")}</field.Label>
                <field.Content>
                  <form.Subscribe selector={(state) => state.values.priceDisplay}>
                    {(priceDisplay) => (
                      <HuAutonomousPurchaseForm
                        setAutonomousPurchase={(autonomousPurchase) => {
                          if (autonomousPurchase) field.handleChange(autonomousPurchase)
                        }}
                        autonomousPurchase={field.state.value}
                        isPriceDisplay={priceDisplay}
                      />
                    )}
                  </form.Subscribe>
                  <field.Message />
                </field.Content>
              </field.Field>
            )}
          </form.AppField>
        </div>

        <div className="mt-5 mb-4 flex gap-5">
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setPageNumber((prev) => prev - 1)
            }}
            className="grow px-3 py-2"
          >
            <ArrowLeftIcon color="currentColor" />
            {word("global.previous")}
          </Button>
          <Button variant="primary" size="lg" onClick={handleSubmit} className="grow px-3 py-2">
            {word("global.next")}
            <ArrowRightIcon color="currentColor" />
          </Button>
        </div>
      </div>
    )
  },
})
